import React, { useCallback, useEffect, useRef } from 'react';

// Styles & Assets
import styles from './MultiRangeSlider.module.css';

// Models
interface Props {
    min: number,
    max: number,
    minValue?: number,
    maxValue?: number,
    labelMin?: (min: number) => string
    labelMax?: (max: number) => string
    onChange: ({ min, max }: { min: number, max: number }) => void
}

const MultiRangeSlider: React.FC<Props> = ({ min, max, minValue = min, maxValue = max, labelMin, labelMax, onChange }) => {

    const minValRef = useRef(minValue);
    const maxValRef = useRef(maxValue);
    const range = useRef<HTMLDivElement | null>(null);

    // Convert to percentage
    const getPercent = useCallback((value: number) => (
        Math.round(((value - min) / (max - min)) * 100)
    ), [min, max]);

    // Set width of the range to decrease from the left side
    useEffect(() => {
        const minPercent = getPercent(minValue);
        const maxPercent = getPercent(maxValRef.current);

        if (range.current) {
            range.current.style.left = `${minPercent}%`;
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [minValue, getPercent]);

    // Set width of the range to decrease from the right side
    useEffect(() => {
        const minPercent = getPercent(minValRef.current);
        const maxPercent = getPercent(maxValue);

        if (range.current) {
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [maxValue, getPercent]);

    // Get min and max values when their state changes
    const handleChange = (type: 'min' | 'max') => (e: React.ChangeEvent<HTMLInputElement>) => {
        if (type === 'min') {
            const value = Math.min(Number(e.target.value), maxValue - 1);
            onChange({ min: value, max: maxValue });
            minValRef.current = value;
        }
        if (type === 'max') {
            const value = Math.max(Number(e.target.value), minValue + 1);
            onChange({ min: minValue, max: value });
            maxValRef.current = value;
        }
    };

    return (
        <div className={styles.container}>
            <input
                type="range"
                min={min}
                max={max}
                value={minValue}
                onChange={handleChange('min')}
                className={`${styles.thumb} ${styles.thumb__left}`}
                style={{ zIndex: minValue > max - 100 ? '5' : undefined }}
            />
            <input
                type="range"
                min={min}
                max={max}
                value={maxValue}
                onChange={handleChange('max')}
                className={`${styles.thumb} ${styles.thumb__right}`}
            />

            <div className={styles.slider}>
                <div className={styles.slider__track} />
                <div ref={range} className={styles.slider__range} />
                <div className={styles.slider__left__value}>{labelMin?.(minValue) ?? minValue}</div>
                <div className={styles.slider__right__value}>{labelMax?.(maxValue) ?? maxValue}</div>
            </div>
        </div>
    );
};

export default MultiRangeSlider;